import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ElementRef,
  HostListener,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  forwardRef,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/operator/takeUntil';

/**
 * Shape of each item passed in via [items].
 * The actual property names are configurable via [idField] / [textField].
 */
export interface SelectItem {
  [key: string]: any;
}

export const SEARCHABLE_MULTI_SELECT_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => SearchableMultiSelectComponent),
  multi: true,
};

@Component({
  selector: 'searchable-multi-select',
  template: require('./searchable-multi-select.component.html'),
  styles: [require('./searchable-multi-select.component.scss')],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [SEARCHABLE_MULTI_SELECT_VALUE_ACCESSOR],
})
export class SearchableMultiSelectComponent
  implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {

  // ─── Inputs ────────────────────────────────────────────────────────────────

  /** Full list of items to display. */
  @Input() items: SelectItem[] = [];

  /** Placeholder shown inside the trigger when no items are pending. */
  @Input() placeholder = 'Search and select…';

  /** Property name on each item used as the stored value (emitted to the form). */
  @Input() idField = 'id';

  /** Property name on each item used as the display label. */
  @Input() textField = 'name';

  /** When true the control is read-only and the panel cannot be opened. */
  @Input() disabled = false;

  // ─── Outputs ───────────────────────────────────────────────────────────────

  /** Emits the array of confirmed IDs whenever the confirmed selection changes. */
  @Output() selectionChange = new EventEmitter<any[]>();

  // ─── Internal state ────────────────────────────────────────────────────────

  isOpen = false;
  searchQuery = '';

  /**
   * Items currently visible in the dropdown list.
   * Derived from availableItems by applyFilter(); never includes confirmed items.
   */
  filteredItems: SelectItem[] = [];

  /**
   * CONFIRMED selection — items that have been committed via "Select Customers".
   * These items are removed from the dropdown and displayed in the Selected
   * Items list below the component.
   * This is the only state written to the Reactive Form.
   */
  selectedIds = new Set<any>();

  /**
   * TEMPORARY/PENDING selection — checkboxes ticked inside the open panel
   * but not yet confirmed.
   * Initialised as an empty set on each panel open (pending ≠ confirmed).
   * Promoted into selectedIds when the user clicks "Select Customers".
   * Discarded (reset to empty) on Escape / outside-click / Tab.
   */
  pendingIds = new Set<any>();

  // ─── ControlValueAccessor callbacks ────────────────────────────────────────

  private onChange: (value: any[]) => void = () => {};
  private onTouched: () => void = () => {};

  // ─── Lifecycle ─────────────────────────────────────────────────────────────

  private destroy$ = new Subject<void>();

  constructor(
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.rebuildAvailable();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items']) {
      this.rebuildAvailable();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ─── ControlValueAccessor ──────────────────────────────────────────────────

  /** Called by Angular when the form control value is set programmatically. */
  writeValue(ids: any[]): void {
    this.selectedIds = new Set(Array.isArray(ids) ? ids : []);
    this.pendingIds = new Set<any>();
    this.rebuildAvailable();
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: any[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  // ─── Panel open / close ────────────────────────────────────────────────────

  togglePanel(): void {
    if (this.disabled) { return; }
    this.isOpen ? this.closePanel() : this.openPanel();
  }

  openPanel(): void {
    this.isOpen = true;
    this.searchQuery = '';
    // Pending starts empty each time — user must re-select within this session.
    this.pendingIds = new Set<any>();
    this.rebuildAvailable();
    this.cdr.markForCheck();
  }

  closePanel(): void {
    this.isOpen = false;
    // Discard any pending (un-confirmed) ticks.
    this.pendingIds = new Set<any>();
    this.onTouched();
    this.cdr.markForCheck();
  }

  /**
   * Promote pendingIds into selectedIds, update the form, rebuild the
   * available list, and close the panel.
   * This is the ONLY path that writes a new value to the Reactive Form.
   */
  confirmSelection(): void {
    // Merge pending into confirmed.
    this.pendingIds.forEach(id => this.selectedIds.add(id));
    this.selectedIds = new Set(this.selectedIds);   // new ref for OnPush
    this.pendingIds = new Set<any>();

    this.emitChange();
    this.rebuildAvailable();

    this.isOpen = false;
    this.onTouched();
    this.cdr.markForCheck();
  }

  /**
   * Remove a single customer from the confirmed list.
   * Makes them immediately available again in the dropdown.
   */
  removeConfirmed(id: any): void {
    this.selectedIds.delete(id);
    this.selectedIds = new Set(this.selectedIds);   // new ref for OnPush
    // Also remove from pending in case they were re-ticked while panel was open.
    this.pendingIds.delete(id);
    this.pendingIds = new Set(this.pendingIds);

    this.emitChange();
    this.rebuildAvailable();
    this.cdr.markForCheck();
  }

  // ─── Outside-click / keyboard ──────────────────────────────────────────────

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.isOpen && !this.elementRef.nativeElement.contains(event.target)) {
      this.closePanel();
    }
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' || event.key === 'Esc') {
      this.closePanel();
    }
    if (event.key === 'Tab' && this.isOpen) {
      this.closePanel();
    }
  }

  // ─── Search ────────────────────────────────────────────────────────────────

  onSearchChange(query: string): void {
    this.searchQuery = query;
    this.applyFilter();
  }

  // ─── Selection ─────────────────────────────────────────────────────────────

  /** True if the item is TEMPORARILY checked (pending, not confirmed). */
  isSelected(item: SelectItem): boolean {
    return this.pendingIds.has(item[this.idField]);
  }

  /** Toggles an item in pendingIds only. Does NOT touch the form. */
  toggleItem(item: SelectItem): void {
    const id = item[this.idField];
    if (this.pendingIds.has(id)) {
      this.pendingIds.delete(id);
    } else {
      this.pendingIds.add(id);
    }
    this.pendingIds = new Set(this.pendingIds);
    this.cdr.markForCheck();
  }

  // ─── Computed helpers ──────────────────────────────────────────────────────

  /**
   * Customers that have been confirmed — displayed in the Selected Items list.
   * Ordered by original items[] position for stable rendering.
   */
  get confirmedItems(): SelectItem[] {
    return this.items.filter(item => this.selectedIds.has(item[this.idField]));
  }

  /** Count of pending (in-panel) ticks — shown next to the Confirm button. */
  get pendingCount(): number {
    return this.pendingIds.size;
  }

  /** Used by *ngFor to avoid re-rendering unchanged rows. */
  trackById(_index: number, item: SelectItem): any {
    return item[this.idField];
  }

  // ─── Private helpers ───────────────────────────────────────────────────────

  /**
   * Rebuild filteredItems from items[], excluding already-confirmed entries.
   * Called after any change to selectedIds or items[].
   */
  private rebuildAvailable(): void {
    const available = this.items.filter(
      item => !this.selectedIds.has(item[this.idField]),
    );
    const q = (this.searchQuery || '').trim().toLowerCase();
    if (!q) {
      this.filteredItems = available;
    } else {
      this.filteredItems = available.filter(item =>
        String(item[this.textField] || '').toLowerCase().includes(q),
      );
    }
    this.cdr.markForCheck();
  }

  /** Filters available (non-confirmed) items by the current search query. */
  private applyFilter(): void {
    this.rebuildAvailable();
  }

  private emitChange(): void {
    const ids = Array.from(this.selectedIds);
    this.onChange(ids);
    this.selectionChange.emit(ids);
  }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./src/app/shared/searchable-multi-select/searchable-multi-select.component.ts